/**
 * Gráfico de histórico de saldo e fluxos realizados.
 * Visual alinhado ao BalanceProjectionChart, sem projeção futura.
 */

import React, { useMemo, useState, useId } from 'react'
import '../BalanceProjectionChart/BalanceProjectionChart.css'
import './BalanceHistoryChart.css'

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function getCurvePath(data, key) {
  if (!data.length) return ''
  let path = `M ${data[0].x},${data[0][key]}`
  for (let i = 0; i < data.length - 1; i++) {
    const p0 = data[i]
    const p1 = data[i + 1]
    const cp1x = p0.x + (p1.x - p0.x) / 2
    path += ` C ${cp1x},${p0[key]} ${cp1x},${p1[key]} ${p1.x},${p1[key]}`
  }
  return path
}

function buildChartModel(balanceHistory) {
  const rawDays = balanceHistory?.days || []
  if (!rawDays.length) {
    return { chartData: [], inflowPath: '', outflowPath: '', balancePath: '' }
  }

  const values = rawDays.flatMap((d) => [
    Number(d.balance) || 0,
    Number(d.inflow) || 0,
    Number(d.outflow) || 0
  ])
  const maxVal = Math.max(...values, 1) * 1.1
  const n = rawDays.length

  const chartData = rawDays.map((day, i) => {
    const balance = Number(day.balance) || 0
    const inflow = Number(day.inflow) || 0
    const outflow = Number(day.outflow) || 0
    return {
      ...day,
      balance,
      inflow,
      outflow,
      x: n === 1 ? 500 : (i / (n - 1)) * 1000,
      yBalance: 300 - (balance / maxVal) * 260,
      yInflow: 300 - (inflow / maxVal) * 260,
      yOutflow: 300 - (outflow / maxVal) * 260
    }
  })

  return {
    chartData,
    inflowPath: getCurvePath(chartData, 'yInflow'),
    outflowPath: getCurvePath(chartData, 'yOutflow'),
    balancePath: getCurvePath(chartData, 'yBalance')
  }
}

function BalanceHistoryChart({ balanceHistory, periodLabel }) {
  const chartId = useId().replace(/:/g, '')
  const [hoveredPoint, setHoveredPoint] = useState(null)
  const { chartData, inflowPath, outflowPath, balancePath } = useMemo(
    () => buildChartModel(balanceHistory),
    [balanceHistory]
  )

  const glowFilterId = `balance-history-glow-${chartId}`
  const balanceGradId = `balance-history-grad-${chartId}`

  const labelStep = Math.max(1, Math.ceil(chartData.length / 8))

  return (
    <div className="balance-history-chart balance-projection-chart cash-flow-analysis-balance-chart">
      <div className="cash-flow-analysis-balance-chart-container balance-projection-chart-wrap">
        <div className="cash-flow-analysis-balance-chart-header">
          <div>
            <h3 className="cash-flow-analysis-balance-chart-title">
              Histórico de Saldo & Fluxos
            </h3>
            <p className="cash-flow-analysis-balance-chart-subtitle">
              Movimentação realizada no período
              {periodLabel ? ` · ${periodLabel}` : ''}
            </p>
          </div>
          <div className="cash-flow-analysis-balance-chart-legend">
            <div className="cash-flow-analysis-balance-chart-legend-item">
              <div className="cash-flow-analysis-balance-chart-legend-color balance" />
              <span>Saldo</span>
            </div>
            <div className="cash-flow-analysis-balance-chart-legend-item">
              <div className="cash-flow-analysis-balance-chart-legend-color inflow" />
              <span>Receitas</span>
            </div>
            <div className="cash-flow-analysis-balance-chart-legend-item">
              <div className="cash-flow-analysis-balance-chart-legend-color outflow" />
              <span>Despesas</span>
            </div>
          </div>
        </div>

        <div className="cash-flow-analysis-balance-chart-content">
          {chartData.length > 0 ? (
            <>
              <div className="cash-flow-analysis-balance-chart-grid" />
              <svg
                viewBox="0 0 1000 300"
                className="cash-flow-analysis-balance-chart-svg"
                preserveAspectRatio="none"
              >
                <defs>
                  <filter id={glowFilterId}>
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                  <linearGradient id={balanceGradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d={`${balancePath} L 1000,300 L 0,300 Z`}
                  fill={`url(#${balanceGradId})`}
                  className="cash-flow-analysis-balance-chart-area"
                />
                <path
                  d={inflowPath}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="0.8"
                  strokeOpacity="0.4"
                  strokeDasharray="4 2"
                />
                <path
                  d={outflowPath}
                  fill="none"
                  stroke="#f43f5e"
                  strokeWidth="0.8"
                  strokeOpacity="0.4"
                  strokeDasharray="4 2"
                />
                <path
                  d={balancePath}
                  fill="none"
                  stroke="#16a34a"
                  strokeWidth="3"
                  filter={`url(#${glowFilterId})`}
                  className="cash-flow-analysis-balance-chart-line"
                />
                {chartData.map((d, i) => (
                  <rect
                    key={d.date || d.label || i}
                    x={d.x - (1000 / Math.max(chartData.length, 1)) / 2}
                    y={0}
                    width={1000 / Math.max(chartData.length, 1)}
                    height={300}
                    fill="transparent"
                    className="cash-flow-analysis-balance-chart-hitbox"
                    onMouseEnter={() => setHoveredPoint(d)}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                ))}
                {hoveredPoint && (
                  <g className="cash-flow-analysis-balance-chart-hover">
                    <line
                      x1={hoveredPoint.x}
                      y1={0}
                      x2={hoveredPoint.x}
                      y2={300}
                      stroke="#94a3b8"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                    />
                    <circle
                      cx={hoveredPoint.x}
                      cy={hoveredPoint.yBalance}
                      r="5"
                      fill="#16a34a"
                      stroke="#fff"
                      strokeWidth="2"
                    />
                  </g>
                )}
              </svg>

              {hoveredPoint && (
                <div
                  className={`cash-flow-analysis-balance-chart-tooltip${
                    hoveredPoint.x / 1000 >= 0.55
                      ? ' is-align-left'
                      : ' is-align-right'
                  }`}
                  style={{
                    left: `${(hoveredPoint.x / 1000) * 100}%`
                  }}
                >
                  <div className="cash-flow-analysis-balance-chart-tooltip-header">
                    <span className="cash-flow-analysis-balance-chart-tooltip-label">Dia</span>
                    <span className="cash-flow-analysis-balance-chart-tooltip-date">
                      {hoveredPoint.label || hoveredPoint.date}
                    </span>
                  </div>
                  <div className="cash-flow-analysis-balance-chart-tooltip-content">
                    <div className="cash-flow-analysis-balance-chart-tooltip-item">
                      <p className="cash-flow-analysis-balance-chart-tooltip-item-label">Saldo</p>
                      <p className="cash-flow-analysis-balance-chart-tooltip-item-value">
                        {formatCurrency(hoveredPoint.balance)}
                      </p>
                    </div>
                    <div className="cash-flow-analysis-balance-chart-tooltip-details">
                      <p className="cash-flow-analysis-balance-chart-tooltip-detail-label inflow">
                        Receitas
                      </p>
                      <p className="cash-flow-analysis-balance-chart-tooltip-detail-value">
                        {formatCurrency(hoveredPoint.inflow)}
                      </p>
                      <p className="cash-flow-analysis-balance-chart-tooltip-detail-label outflow">
                        Despesas
                      </p>
                      <p className="cash-flow-analysis-balance-chart-tooltip-detail-value">
                        {formatCurrency(hoveredPoint.outflow)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="cash-flow-analysis-balance-chart-labels">
                {chartData.map((d, i) => (
                  i % labelStep === 0 || i === chartData.length - 1 ? (
                    <span key={d.date || d.label || i} className="cash-flow-analysis-balance-chart-label">
                      {d.label}
                    </span>
                  ) : (
                    <span key={d.date || d.label || i} className="cash-flow-analysis-balance-chart-label is-spacer" />
                  )
                ))}
              </div>
            </>
          ) : (
            <div className="cash-flow-analysis-balance-chart-empty">
              Sem movimentação no período selecionado.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BalanceHistoryChart
